import { useState, useCallback } from "react";
import { Recipe, Meal } from "../types";
import {
  generateMealPlan,
  findReplacementRecipe,
  PlannedMeal,
} from "../util/meal-plan-generator";
import { useRecipeManager } from "../context-providers/recipe-manager-provider";
import { useMealPlanner } from "../context-providers/meal-planner-provider";

export function useMealPlanGenerator() {
  const { userRecipes, homeRecipes } = useRecipeManager();
  const { userMeals, homeMeals } = useMealPlanner();

  const [generatedPlan, setGeneratedPlan] = useState<PlannedMeal[] | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [rerolledRecipeIds, setRerolledRecipeIds] = useState<string[]>([]); // Track recipes that have been rerolled

  // Combine user and home recipes
  const allRecipes = [...(userRecipes || []), ...(homeRecipes || [])];

  // Combine user and home meals
  const allMeals = [...(userMeals || []), ...(homeMeals || [])];

  /**
   * Generate a meal plan for the specified date range
   */
  const generate = useCallback(
    (startDate: Date, endDate: Date) => {
      setIsGenerating(true);
      setError(null);

      try {
        // Validate inputs
        if (!startDate || !endDate) {
          throw new Error("Start date and end date are required");
        }

        if (startDate > endDate) {
          throw new Error("Start date must be before end date");
        }

        if (allRecipes.length === 0) {
          throw new Error(
            "No recipes available. Please add some recipes first."
          );
        }

        // Generate the plan
        const plan = generateMealPlan(allRecipes, allMeals, startDate, endDate);

        setGeneratedPlan(plan);
        setIsGenerating(false);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate meal plan";
        setError(errorMessage);
        setGeneratedPlan(null);
        setIsGenerating(false);
      }
    },
    [allRecipes, allMeals]
  );

  /**
   * Clear the generated plan
   */
  const clear = useCallback(() => {
    setGeneratedPlan(null);
    setError(null);
    setRerolledRecipeIds([]);
  }, []);

  /**
   * Regenerate the current plan (if one exists)
   */
  const regenerate = useCallback(() => {
    if (generatedPlan && generatedPlan.length > 0) {
      const startDate = generatedPlan[0].date;
      const endDate = generatedPlan[generatedPlan.length - 1].date;
      setRerolledRecipeIds([]); // Clear reroll history
      generate(startDate, endDate);
    }
  }, [generatedPlan, generate]);

  /**
   * Reroll a specific recipe in the plan
   */
  const rerollRecipe = useCallback(
    (index: number) => {
      if (!generatedPlan || index < 0 || index >= generatedPlan.length) return;

      const currentRecipe = generatedPlan[index].recipe;
      const selectedRecipes = generatedPlan
        .filter((_, i) => i !== index)
        .map((pm) => pm.recipe);

      // Find a replacement, excluding recipes that have been rerolled away
      const replacement = findReplacementRecipe(
        currentRecipe,
        allRecipes,
        selectedRecipes,
        allMeals,
        rerolledRecipeIds
      );

      if (replacement) {
        // Update the plan with the new recipe
        const newPlan = [...generatedPlan];
        newPlan[index] = {
          ...newPlan[index],
          recipe: replacement,
        };
        setGeneratedPlan(newPlan);

        // Track the replaced recipe so we don't suggest it again
        if (currentRecipe.recipeId) {
          setRerolledRecipeIds((prev) => [...prev, currentRecipe.recipeId!]);
        }
      } else {
        setError("No more suitable recipes available for this slot");
      }
    },
    [generatedPlan, allRecipes, allMeals, rerolledRecipeIds]
  );

  /**
   * Replace a meal at a specific index with a chosen recipe
   */
  const replaceMealWithRecipe = useCallback(
    (index: number, recipe: Recipe) => {
      if (!generatedPlan) return;
      if (index < 0 || index >= generatedPlan.length) return;

      const newPlan = [...generatedPlan];
      newPlan[index] = {
        ...newPlan[index],
        recipe: recipe,
      };
      setGeneratedPlan(newPlan);
    },
    [generatedPlan]
  );

  /**
   * Reorder the plan by moving a recipe from one index to another
   */
  const reorderPlan = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!generatedPlan || fromIndex === toIndex) return;
      if (fromIndex < 0 || fromIndex >= generatedPlan.length) return;
      if (toIndex < 0 || toIndex >= generatedPlan.length) return;

      const newPlan = [...generatedPlan];
      const [movedItem] = newPlan.splice(fromIndex, 1);
      newPlan.splice(toIndex, 0, movedItem);

      // Update dates to reflect new order
      for (let i = 0; i < newPlan.length; i++) {
        const date = new Date(generatedPlan[0].date);
        date.setDate(date.getDate() + i);
        newPlan[i].date = date;
      }

      setGeneratedPlan(newPlan);
    },
    [generatedPlan]
  );

  return {
    generatedPlan,
    error,
    isGenerating,
    generate,
    clear,
    regenerate,
    rerollRecipe,
    replaceMealWithRecipe,
    reorderPlan,
  };
}
